/* ============================================================================
   Bid Manager access levels. The studio manager can do everything; invited
   tender-team members act at their role's level. The table below is exactly
   what the pages enforce — it renders on Team & Invites.
   ========================================================================== */
import type { Session } from "./auth";

export type BidLevel = "manager" | "admin" | "coordinator" | "viewer" | "none";

export function bidLevel(s: Session | null): BidLevel {
  if (!s) return "none";
  if (s.role === "manager") return "manager";
  if (s.role === "bidteam") return s.teamRole;
  return "none";
}

/** Day-to-day tender work: documents, addenda, RFI answers, alerts, RFQs,
    bidder invites, ITT drafting. */
export function bidCanAct(s: Session | null): boolean {
  const l = bidLevel(s);
  return l === "manager" || l === "admin" || l === "coordinator";
}

/** Decisive moves: opening/awarding bids, issuing the ITT, the draft
    contract, selecting RFQ winners, and managing the team itself. */
export function bidCanAward(s: Session | null): boolean {
  const l = bidLevel(s);
  return l === "manager" || l === "admin";
}

export const BID_LEVEL_LABEL: Record<Exclude<BidLevel, "none">, string> = {
  manager: "Studio Manager",
  admin: "Tender Admin",
  coordinator: "Tender Coordinator",
  viewer: "Viewer",
};

/** Capability rows for the Team page permission table (col order: admin, coordinator, viewer). */
export const BID_PERMISSIONS: { label: string; admin: boolean; coordinator: boolean; viewer: boolean }[] = [
  { label: "View every section of the tender room", admin: true, coordinator: true, viewer: true },
  { label: "Add / upload documents & drawing sets, issue addenda", admin: true, coordinator: true, viewer: false },
  { label: "Answer RFIs and circulate clarifications", admin: true, coordinator: true, viewer: false },
  { label: "Compose & send email alerts", admin: true, coordinator: true, viewer: false },
  { label: "Create & issue supply quotation packages, record quotes", admin: true, coordinator: true, viewer: false },
  { label: "Invite bidders & resend access codes", admin: true, coordinator: true, viewer: false },
  { label: "Draft & save the Invitation to Tender", admin: true, coordinator: true, viewer: false },
  { label: "Issue the ITT to bidders", admin: true, coordinator: false, viewer: false },
  { label: "Open sealed bids, share results, award", admin: true, coordinator: false, viewer: false },
  { label: "Import / replace the draft contract", admin: true, coordinator: false, viewer: false },
  { label: "Select RFQ winning quotes", admin: true, coordinator: false, viewer: false },
  { label: "Manage the tender team", admin: true, coordinator: false, viewer: false },
];
